import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Separator } from '@vaiu/ui/separator';
import { Badge } from '@vaiu/ui/badge';
import { Input } from '@vaiu/ui/input';
import { Textarea } from '@vaiu/ui/textarea';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { 
  ArrowLeft, 
  HelpCircle, 
  BookOpen, 
  MessageSquare, 
  Mail,
  ExternalLink,
  Rocket,
  Shield,
  CreditCard,
  Users,
  Zap,
  FileText,
  Send,
  ThumbsUp,
  ThumbsDown,
  Vote,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@vaiu/ui/tabs";
import ReleaseNotes from '@/components/ReleaseNotes';
import { i18n } from '@/utils/i18n';
import { useAuth } from '@/context/AuthContext';

interface UserVote {
  id: string;
  question: string;
  isActive: boolean;
  totalVotes: number;
  yesVotes: number;
  noVotes: number;
  createdAt: string;
  userHasVoted?: boolean;
  userVote?: 'yes' | 'no';
}

const HelpPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  // Feedback form state
  const [feedbackForm, setFeedbackForm] = useState({
    subject: '',
    message: '',
    email: ''
  });
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);

  // User vote state
  const [currentVote, setCurrentVote] = useState<UserVote | null>(null);
  const [voteLoading, setVoteLoading] = useState(false);
  const [hasVoted, setHasVoted] = useState(false);

  // Start app tour function
  const startAppTour = () => {
    if (user) {
      // Clear any existing tour status to allow retaking
      localStorage.removeItem(`tour_shown_${user._id}`);
      
      // Emit the custom event that the AppTour component listens to
      window.dispatchEvent(new CustomEvent('startAppTour'));
      
      // Navigate back to main page where the tour will start
      navigate('/');
      
      toast({
        title: i18n.__('help.toast.tour'),
        description: i18n.__('help.toast.tourDesc'),
      });
    }
  };

  const faqItems = [
    {
      question: i18n.__('help.faq.howToCreateToken'),
      answer: i18n.__('help.faq.howToCreateTokenDesc'),
      category: "payments"
    },
    {
      question: i18n.__('help.faq.tokenDifference'),
      answer: i18n.__('help.faq.tokenDifferenceDesc'),
      category: "payments"
    },
    {
      question: i18n.__('help.faq.referralTokens'),
      answer: i18n.__('help.faq.referralTokensDesc'),
      category: "referrals"
    },
    {
      question: i18n.__('help.faq.supportedMethods'),
      answer: i18n.__('help.faq.supportedMethodsDesc'),
      category: "payments"
    },
    {
      question: i18n.__('help.faq.security'),
      answer: i18n.__('help.faq.securityDesc'),
      category: "security"
    },
    {
      question: i18n.__('vaiu.faq.cancel_token_question'),
      answer: i18n.__('vaiu.faq.cancel_token_answer'),
      category: "payments"
    },
    {
      question: i18n.__('help.faq.fees'),
      answer: i18n.__('help.faq.feesDesc'),
      category: "general"
    },
    {
      question: i18n.__('help.faq.addMoney'),
      answer: i18n.__('help.faq.addMoneyDesc'),
      category: "payments"
    },
    {
      question: i18n.__('help.faq.data'),
      answer: i18n.__('help.faq.dataDesc'),
      category: "privacy"
    },
    {
      question: i18n.__('help.faq.cookies'),
      answer: i18n.__('help.faq.cookiesDesc'),
      category: "privacy"
    },
    {
      question: i18n.__('help.faq.optOut'),
      answer: i18n.__('help.faq.optOutDesc'),
      category: "privacy"
    }
  ];

  const quickActions = [
    {
      title: i18n.__('help.quickStart.tour'),
      description: i18n.__('help.quickStart.tourDesc'),
      icon: <HelpCircle className="w-5 h-5" />,
      action: startAppTour,
      color: "bg-indigo-500"
    },
    {
      title: i18n.__('help.quickStart.send'),
      description: i18n.__('help.quickStart.sendDesc'),
      icon: <CreditCard className="w-5 h-5" />,
      action: () => navigate('/'),
      color: "bg-green-500"
    },
    {
      title: i18n.__('help.quickStart.request'),
      description: i18n.__('help.quickStart.requestDesc'),
      icon: <FileText className="w-5 h-5" />,
      action: () => navigate('/'),
      color: "bg-blue-500"
    },
    {
      title: i18n.__('help.quickStart.referral'),
      description: i18n.__('help.quickStart.referralDesc'),
      icon: <Users className="w-5 h-5" />,
      action: () => navigate('/referral-tokens'),
      color: "bg-purple-500"
    },
    {
      title: i18n.__('help.quickStart.sources'),
      description: i18n.__('help.quickStart.sourcesDesc'),
      icon: <Zap className="w-5 h-5" />,
      action: () => navigate('/payment-sources'),
      color: "bg-orange-500"
    }
  ];

  const supportChannels = [
    {
      title: i18n.__('help.support.email'),
      description: i18n.__('help.support.emailDesc'),
      icon: <Mail className="w-5 h-5" />,
      contact: "support@vaiuglobal.com",
      color: "bg-blue-500"
    },
    {
      title: i18n.__('help.support.chat'),
      description: i18n.__('help.support.chatDesc'),
      icon: <MessageSquare className="w-5 h-5" />,
      contact: i18n.__('help.support.chatHours'),
      color: "bg-green-500"
    },
    {
      title: i18n.__('help.support.docs'),
      description: i18n.__('help.support.docsDesc'),
      icon: <BookOpen className="w-5 h-5" />,
      contact: "help.vaiuglobal.com",
      color: "bg-purple-500"
    }
  ];

  const filterFAQ = (category: string) => {
    if (category === 'all') return faqItems;
    return faqItems.filter(item => item.category === category);
  };

  const [selectedFAQCategory, setSelectedFAQCategory] = useState('all');

  // Load current user vote on component mount
  useEffect(() => {
    loadCurrentVote();
  }, []);

  const loadCurrentVote = async () => {
    try {
      // Simulate API call - replace with actual implementation
      const mockVote: UserVote = {
        id: 'vote-1',
        question: 'Would you recommend adding a dark mode option to the app?',
        isActive: true,
        totalVotes: 147,
        yesVotes: 103,
        noVotes: 44,
        createdAt: '2025-01-15T10:00:00Z',
        userHasVoted: false
      };
      
      setCurrentVote(mockVote);
    } catch (error) {
      console.error('Failed to load current vote:', error);
    }
  };

  const submitFeedback = async () => {
    if (!feedbackForm.subject.trim() || !feedbackForm.message.trim()) {
      toast({
        title: i18n.__('help.toast.missingInfo'),
        description: i18n.__('help.toast.missingInfoDesc'),
        variant: 'destructive',
      });
      return;
    }

    // Email validation if provided
    if (feedbackForm.email && !feedbackForm.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: i18n.__('help.toast.invalidEmail'),
        description: i18n.__('help.toast.invalidEmailDesc'),
        variant: 'destructive',
      });
      return;
    }

    setFeedbackLoading(true);
    try {
      const response = await platformApi.support.createTicket({
        email: feedbackForm.email || user?.email || 'anonymous@vaiu.app',
        subject: feedbackForm.subject,
        message: feedbackForm.message,
        category: 'feedback',
        priority: 'medium',
        appVersion: (typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : '1.0.0')
      });
      
      setFeedbackSent(true);
      setFeedbackForm({ subject: '', message: '', email: '' });
      
      toast({
        title: i18n.__('help.toast.feedbackSent'),
        description: response.message || i18n.__('help.toast.feedbackSentDesc'),
      });

      // Show ticket number if available
      if (response.ticket?.ticketNumber) {
        toast({
          title: i18n.__('help.toast.refNum'),
          description: i18n.__('help.toast.refNumDesc', { ticketNumber: response.ticket.ticketNumber }),
        });
      }
    } catch (error: any) {
      console.error('Failed to submit feedback:', error);
      toast({
        title: i18n.__('common.error'),
        description: error.message || i18n.__('help.toast.feedbackError'),
        variant: 'destructive',
      });
    } finally {
      setFeedbackLoading(false);
    }
  };

  const submitVote = async (vote: 'yes' | 'no') => {
    if (!currentVote || hasVoted) return;

    setVoteLoading(true);
    try {
      // Simulate API call - replace with actual implementation
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Update vote counts
      const updatedVote = {
        ...currentVote,
        totalVotes: currentVote.totalVotes + 1,
        yesVotes: vote === 'yes' ? currentVote.yesVotes + 1 : currentVote.yesVotes,
        noVotes: vote === 'no' ? currentVote.noVotes + 1 : currentVote.noVotes,
        userHasVoted: true,
        userVote: vote
      };
      
      setCurrentVote(updatedVote);
      setHasVoted(true);
      
      toast({
        title: i18n.__('help.toast.voteRecorded'),
        description: i18n.__('help.toast.voteRecordedDesc', { vote: vote.toUpperCase() }),
      });
    } catch (error) {
      toast({
        title: i18n.__('common.error'),
        description: i18n.__('help.toast.voteError'),
        variant: 'destructive',
      });
    } finally {
      setVoteLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(-1)}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {i18n.__('help.back')}
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{i18n.__('help.title')}</h1>
            <p className="text-gray-600">{i18n.__('help.subtitle')}</p>
          </div>
        </div>

        {/* Prominent Features Section */}
        <div className="space-y-6 mb-8">
          {/* User Vote Section */}
          {currentVote && currentVote.isActive && (
            <Card className="border-2 border-primary/20 bg-gradient-to-r from-blue-50 to-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Vote className="w-5 h-5 text-primary" />
                  <span>{i18n.__('help.communityVoice')}</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                    {i18n.__('help.votes', { count: currentVote.totalVotes })}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {i18n.__('help.shapeTheFuture')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-lg font-medium">{currentVote.question}</p>
                  
                  {currentVote.userHasVoted || hasVoted ? (
                    <div className="space-y-3">
                      <Alert>
                        <CheckCircle className="h-4 w-4" />
                        <AlertDescription>
                          {i18n.__('help.voted')}
                        </AlertDescription>
                      </Alert>
                      
                      {/* Vote Results */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>{i18n.__('help.yesVotes', { yes: currentVote.yesVotes, total: currentVote.totalVotes })}</span>
                          <span>{i18n.__('help.noVotes', { no: currentVote.noVotes, total: currentVote.totalVotes })}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-green-500 h-3 rounded-l-full transition-all duration-300"
                            style={{ width: `${(currentVote.yesVotes / currentVote.totalVotes) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex space-x-3">
                      <Button
                        onClick={() => submitVote('yes')}
                        disabled={voteLoading}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                      >
                        <ThumbsUp className="w-4 h-4 mr-2" />
                        {i18n.__('help.yes')}
                      </Button>
                      <Button
                        onClick={() => submitVote('no')}
                        disabled={voteLoading}
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      >
                        <ThumbsDown className="w-4 h-4 mr-2" />
                        {i18n.__('help.no')}
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Feedback Section */}
          <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-yellow-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-orange-600" />
                <span>{i18n.__('help.sendFeedback')}</span>
              </CardTitle>
              <CardDescription>
                {i18n.__('help.feedbackDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {feedbackSent ? (
                <Alert>
                  <CheckCircle className="h-4 w-4" />
                  <AlertDescription>
                    {i18n.__('help.feedbackSent')}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="feedbackSubject">{i18n.__('help.subject')}</Label>
                      <Input
                        id="feedbackSubject"
                        value={feedbackForm.subject}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, subject: e.target.value })}
                        placeholder={i18n.__('help.subjectPlaceholder')}
                        disabled={feedbackLoading}
                      />
                    </div>
                    <div>
                      <Label htmlFor="feedbackEmail">{i18n.__('help.email')}</Label>
                      <Input
                        id="feedbackEmail"
                        type="email"
                        value={feedbackForm.email}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                        placeholder={i18n.__('help.emailPlaceholder')}
                        disabled={feedbackLoading}
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="feedbackMessage">{i18n.__('help.message')}</Label>
                    <Textarea
                      id="feedbackMessage"
                      value={feedbackForm.message}
                      onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                      placeholder={i18n.__('help.messagePlaceholder')}
                      rows={4}
                      disabled={feedbackLoading}
                    />
                  </div>
                  
                  <Button
                    onClick={submitFeedback}
                    disabled={feedbackLoading || !feedbackForm.subject.trim() || !feedbackForm.message.trim()}
                    className="w-full md:w-auto"
                  >
                    {feedbackLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    {feedbackLoading ? i18n.__('help.sending') : i18n.__('help.send')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="faq" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="faq" className="flex items-center space-x-2">
              <HelpCircle className="w-4 h-4" />
              <span>{i18n.__('help.tabs.faq')}</span>
            </TabsTrigger>
            <TabsTrigger value="releases" className="flex items-center space-x-2">
              <Rocket className="w-4 h-4" />
              <span>{i18n.__('help.tabs.releases')}</span>
            </TabsTrigger>
            <TabsTrigger value="quick-start" className="flex items-center space-x-2">
              <Zap className="w-4 h-4" />
              <span>{i18n.__('help.tabs.quickStart')}</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center space-x-2">
              <MessageSquare className="w-4 h-4" />
              <span>{i18n.__('help.tabs.support')}</span>
            </TabsTrigger>
          </TabsList>

          {/* FAQ Tab */}
          <TabsContent value="faq" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.__('help.faq.title')}</CardTitle>
                <CardDescription>
                  {i18n.__('help.faq.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* FAQ Category Filter */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {['all', 'payments', 'security', 'privacy', 'referrals', 'general'].map((category) => (
                    <Button
                      key={category}
                      variant={selectedFAQCategory === category ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedFAQCategory(category)}
                      className="text-sm"
                    >
                      {i18n.__(`help.faq.categories.${category}`)}
                    </Button>
                  ))}
                </div>

                {/* FAQ Items */}
                <div className="space-y-4">
                  {filterFAQ(selectedFAQCategory).map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <Badge variant="outline" className="mt-1 text-xs">
                          {item.category}
                        </Badge>
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 mb-2">{item.question}</h3>
                          <p className="text-gray-600 text-sm">{item.answer}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Release Notes Tab */}
          <TabsContent value="releases" className="space-y-6">
            <ReleaseNotes />
          </TabsContent>

          {/* Quick Start Tab */}
          <TabsContent value="quick-start" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.__('help.quickStart.title')}</CardTitle>
                <CardDescription>
                  {i18n.__('help.quickStart.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quickActions.map((action, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={action.action}
                    >
                      <div className={`${action.color} text-white p-2 rounded-lg`}>
                        {action.icon}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{action.title}</h3>
                        <p className="text-gray-600 text-sm">{action.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">{i18n.__('help.quickStart.steps')}</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-500 text-white text-sm font-bold rounded-full w-6 h-6 flex items-center justify-center">1</div>
                      <div>
                        <h4 className="font-medium">{i18n.__('help.quickStart.step1')}</h4>
                        <p className="text-gray-600 text-sm">{i18n.__('help.quickStart.step1Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-500 text-white text-sm font-bold rounded-full w-6 h-6 flex items-center justify-center">2</div>
                      <div>
                        <h4 className="font-medium">{i18n.__('help.quickStart.step2')}</h4>
                        <p className="text-gray-600 text-sm">{i18n.__('help.quickStart.step2Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-500 text-white text-sm font-bold rounded-full w-6 h-6 flex items-center justify-center">3</div>
                      <div>
                        <h4 className="font-medium">{i18n.__('help.quickStart.step3')}</h4>
                        <p className="text-gray-600 text-sm">{i18n.__('help.quickStart.step3Desc')}</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="bg-blue-500 text-white text-sm font-bold rounded-full w-6 h-6 flex items-center justify-center">4</div>
                      <div>
                        <h4 className="font-medium">{i18n.__('help.quickStart.step4')}</h4>
                        <p className="text-gray-600 text-sm">{i18n.__('help.quickStart.step4Desc')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{i18n.__('help.support.title')}</CardTitle>
                <CardDescription>
                  {i18n.__('help.support.subtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {supportChannels.map((channel, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 text-center">
                      <div className={`${channel.color} text-white p-3 rounded-lg inline-block mb-3`}>
                        {channel.icon}
                      </div>
                      <h3 className="font-medium text-gray-900 mb-1">{channel.title}</h3>
                      <p className="text-gray-600 text-sm mb-2">{channel.description}</p>
                      <p className="text-blue-600 text-sm font-medium">{channel.contact}</p>
                    </div>
                  ))}
                </div>

                <Separator className="my-6" />

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-blue-900 mb-1">{i18n.__('help.support.securityTitle')}</h3>
                      <p className="text-blue-800 text-sm mb-3">
                        {i18n.__('help.support.securityDesc')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate('/privacy-policy')}
                          className="text-blue-700 border-blue-300 hover:bg-blue-100"
                        >
                          {i18n.__('help.support.privacyPolicy')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate('/cookie-policy')}
                          className="text-blue-700 border-blue-300 hover:bg-blue-100"
                        >
                          {i18n.__('help.support.cookiePolicy')}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate('/cookie-settings')}
                          className="text-blue-700 border-blue-300 hover:bg-blue-100"
                        >
                          {i18n.__('help.support.cookieSettings')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HelpPage; 